import React from 'react';
import { Check, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const Pricing = () => {
  const plans = [
    {
      name: 'Free Score',
      price: '0',
      period: 'forever',
      description: 'Get instant Vastu score',
      features: [
        'Upload floor plan',
        'AI-powered Vastu score (0-100)',
        'Basic compliance overview',
        'Free forever',
      ],
      cta: 'Try Free',
      link: '/upload',
      popular: false,
      highlight: 'Always Free',
    },
    {
      name: 'Full Report',
      price: '199',
      period: 'per report',
      description: 'Complete Vastu analysis',
      features: [
        'Everything in Free',
        'Detailed room-by-room analysis',
        'Specific Vastu doshas identified',
        'Practical remedies & solutions',
        'Direction-wise recommendations',
        'Downloadable PDF report',
      ],
      cta: 'Get Report',
      link: '/upload',
      popular: true,
    },
    {
      name: 'Consultation',
      price: '999',
      period: 'per session',
      description: 'Expert Vastu guidance',
      features: [
        'Everything in Report',
        '30-minute video consultation',
        'Certified Vastu expert',
        'Personalized advice',
        'Answer specific questions',
        'Custom remedy plan',
        'Follow-up support',
      ],
      cta: 'Book Now',
      link: '/consultants',
      popular: false,
      highlight: 'Most Comprehensive',
    },
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-[#F9F6F0] relative overflow-hidden">
      {/* Background drifting blobs */}
      <div className="bg-blob blob-gold top-[10%] left-[-5%] w-[400px] h-[400px] opacity-35" />
      <div className="bg-blob blob-saffron bottom-[15%] right-[-5%] w-[450px] h-[450px] opacity-40" />

      <div className="container mx-auto max-w-6xl px-6 md:px-12 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[10px] font-bold tracking-widest uppercase text-[#D97706] mb-2 block">
            Flexible Plans
          </span>
          <h1 className="text-3xl md:text-5xl font-bold font-vedic text-[#352520] mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-sm md:text-base text-[#5D4E37] max-w-xl mx-auto font-light leading-relaxed">
            Choose the plan that fits your needs. All plans include our core AI analysis.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto items-stretch mb-20">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`bg-white border rounded-3xl p-8 relative flex flex-col justify-between hover-lift transition-all duration-300 ${
                plan.popular 
                  ? 'border-2 border-[#D97706] scale-105 shadow-xl relative z-20' 
                  : 'border-[#EBE6DD]/60 shadow-lg relative z-10'
              }`}
            >
              {/* Highlight Badges */}
              {(plan.popular || plan.highlight) && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-[#D97706] text-white px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-sm whitespace-nowrap">
                    <Sparkles className="w-3.5 h-3.5" />
                    {plan.popular ? 'Most Popular' : plan.highlight}
                  </div>
                </div>
              )}

              <div>
                {/* Plan Info */}
                <div className="mb-6 text-left">
                  <h3 className="text-xl font-bold font-vedic text-[#352520] mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-xs text-[#5D4E37] mb-4 font-light">
                    {plan.description}
                  </p>
                  
                  <div className="flex items-baseline gap-1 mt-4">
                    <span className="text-3xl font-bold text-[#352520]">
                      ₹{plan.price}
                    </span>
                    <span className="text-xs text-[#5D4E37] font-light">
                      /{plan.period}
                    </span>
                  </div>
                </div>

                {/* Features List */}
                <ul className="space-y-3 mb-8 text-left border-t border-[#EBE6DD]/40 pt-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-xs text-[#5D4E37] font-medium">
                      <Check className="w-4 h-4 text-[#D97706] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Button Link */}
              <Link
                to={plan.link}
                className={`block text-center py-3.5 rounded-full font-bold text-xs uppercase tracking-wider transition-all duration-300 shadow-sm active:scale-97 ${
                  plan.popular
                    ? 'bg-[#D97706] hover:bg-[#C25E03] text-white'
                    : 'bg-white border-2 border-[#D97706] hover:border-[#C25E03] text-[#D97706] hover:text-[#C25E03]'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto text-left">
          <h2 className="text-2xl md:text-3xl font-bold font-vedic text-center text-[#352520] mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-white/60 backdrop-blur-md border border-[#EBE6DD]/60 p-6 rounded-2xl shadow-sm">
              <h3 className="font-bold font-vedic text-base text-[#352520] mb-2">Can I cancel anytime?</h3>
              <p className="text-xs md:text-sm text-[#5D4E37] font-light leading-relaxed">
                Yes! All paid plans are billed per report or per session, so there are no recurring subscription fees to cancel. You only pay for what you use.
              </p>
            </div>
            
            <div className="bg-white/60 backdrop-blur-md border border-[#EBE6DD]/60 p-6 rounded-2xl shadow-sm">
              <h3 className="font-bold font-vedic text-base text-[#352520] mb-2">What payment methods do you accept?</h3>
              <p className="text-xs md:text-sm text-[#5D4E37] font-light leading-relaxed">
                We accept all major credit/debit cards, UPI, net banking, and digital wallets securely.
              </p>
            </div>

            <div className="bg-white/60 backdrop-blur-md border border-[#EBE6DD]/60 p-6 rounded-2xl shadow-sm">
              <h3 className="font-bold font-vedic text-base text-[#352520] mb-2">Do you offer refunds?</h3>
              <p className="text-xs md:text-sm text-[#5D4E37] font-light leading-relaxed">
                We offer a 100% money-back guarantee on all report generated within 30 days if you're not satisfied. Simply contact our support team.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Pricing;
